import { useState, useEffect, useCallback } from 'react';
import { Mode } from '../types';
import { IOfficeContext } from '../types/office';
import { getEmailBody, splitHtmlReply } from '../services/office/outlook';
import { getWordDocument } from '../services/office/word';
import { getPowerPointPresentation } from '../services/office/powerpoint';
import { getExcelWorkbook } from '../services/office/excel';
import { logger } from '../utils/logger';

/**
 * Unified hook for getting content from any Office application (Outlook, Word, Excel, PowerPoint)
 * Uses the IOfficeContext interface to work across all Office apps
 */
export function useOfficeContent(
  context: IOfficeContext | null,
  mode: Mode,
  isReady: boolean,
  item: Office.MessageRead | Office.MessageCompose | null
) {
  const [contentText, setContentText] = useState<string>("");
  const [hasOriginalEmail, setHasOriginalEmail] = useState<boolean>(false);

  // Function to update content text from Office context
  // Returns the updated content directly to avoid async state issues
  const updateContentText = useCallback(async (): Promise<string> => {
    try {
      // If we have a context, use the unified interface (works for all Office apps)
      if (context) {
        logger.debug("[useOfficeContent] Using unified context.getContent() for host:", context.host);
        
        // For Word, use the dedicated getWordDocument function
        if (context.host === "Word") {
          logger.debug("[useOfficeContent] Word host detected, using getWordDocument()");
          try {
            const wordContent = await getWordDocument();
            setContentText(wordContent);
            setHasOriginalEmail(false); // Word doesn't have original email concept
            logger.debug("[useOfficeContent] Word content retrieved, length:", wordContent.length);
            return wordContent; // Return the content directly
          } catch (error) {
            logger.error("[useOfficeContent] Error getting Word document:", error);
            // Fallback to context.getContent() if getWordDocument fails
            logger.debug("[useOfficeContent] Falling back to context.getContent()");
          }
        }
        
        // For PowerPoint, use the dedicated getPowerPointPresentation function
        if (context.host === "PowerPoint") {
          logger.debug("[useOfficeContent] PowerPoint host detected, using getPowerPointPresentation()");
          try {
            const pptContent = await getPowerPointPresentation();
            setContentText(pptContent);
            setHasOriginalEmail(false); // PowerPoint doesn't have original email concept
            logger.debug("[useOfficeContent] PowerPoint content retrieved, length:", pptContent.length);
            return pptContent; // Return the content directly
          } catch (error) {
            logger.error("[useOfficeContent] Error getting PowerPoint presentation:", error);
            // Fallback to context.getContent() if getPowerPointPresentation fails
            logger.debug("[useOfficeContent] Falling back to context.getContent()");
          }
        }
        
        // For Excel, use the dedicated getExcelWorkbook function
        if (context.host === "Excel") {
          logger.debug("[useOfficeContent] Excel host detected, using getExcelWorkbook()");
          try {
            const excelContent = await getExcelWorkbook();
            setContentText(excelContent);
            setHasOriginalEmail(false); // Excel doesn't have original email concept
            logger.debug("[useOfficeContent] Excel content retrieved, length:", excelContent.length);
            return excelContent; // Return the content directly
          } catch (error) {
            logger.error("[useOfficeContent] Error getting Excel workbook:", error);
            // Fallback to context.getContent() if getExcelWorkbook fails
            logger.debug("[useOfficeContent] Falling back to context.getContent()");
          }
        }
        
        // For other hosts (Outlook), use context.getContent()
        const content = await context.getContent();
        setContentText(content);
        
        // hasOriginalEmail is only relevant for Outlook compose mode
        if (context.host === "Outlook" && mode === "compose") {
          // For Outlook compose, we need to check for original email in HTML
          try {
            const htmlBody = await getEmailBody(Office.CoercionType.Html);
            const { hasOriginalEmail } = splitHtmlReply(htmlBody);
            setHasOriginalEmail(hasOriginalEmail);
          } catch {
            setHasOriginalEmail(false);
          }
        } else {
          setHasOriginalEmail(false);
        }
        return content; // Return the content directly
      }

      // Fallback: Outlook-specific logic (for backward compatibility)
      if (mode === "read") {
        logger.debug("[useOfficeContent] Fallback: Using Outlook-specific getEmailBody for read mode");
        const body = await getEmailBody();
        setContentText(body);
        setHasOriginalEmail(true);
        return body; // Return the content directly
      } else if (mode === "compose") {
        logger.debug("[useOfficeContent] Fallback: Using Outlook-specific getEmailBody for compose mode");
        // For compose mode, try multiple times with a delay
        let attempts = 0;
        const maxAttempts = 3;
        
        while (attempts < maxAttempts) {
          try {
            const body = await getEmailBody();
            const htmlBody = await getEmailBody(Office.CoercionType.Html);
            
            if (body && body.trim() !== "") {
              setContentText(body);
              
              // Check if there's an original email
              const { hasOriginalEmail } = splitHtmlReply(htmlBody);
              setHasOriginalEmail(hasOriginalEmail);
              return body; // Return the content directly
            }
            
            // If body is empty, wait and retry
            if (attempts < maxAttempts - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (error) {
            logger.warn(`[useOfficeContent] Attempt ${attempts + 1} failed:`, error);
            if (attempts < maxAttempts - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
          attempts++;
        }
        
        logger.warn("[useOfficeContent] All attempts failed to get email body");
        setContentText("");
        setHasOriginalEmail(false);
        return ""; // Return empty string
      } else {
        // For Word/Excel/PowerPoint or null mode, just set empty
        logger.debug("[useOfficeContent] No context and no Outlook mode, setting empty content");
        setContentText("");
        setHasOriginalEmail(false);
        return ""; // Return empty string
      }
    } catch (error) {
      logger.error("[useOfficeContent] Failed to update content:", error);
      setContentText("");
      setHasOriginalEmail(false);
      return ""; // Return empty string on error
    }
  }, [context, mode]);

  // Get current content text, updating if needed
  // Now uses the return value from updateContentText to avoid async state issues
  const getCurrentContentText = useCallback(async (): Promise<string> => {
    // Always call updateContentText to get fresh content, it returns the content directly
    const freshContent = await updateContentText();
    return freshContent || contentText; // Fallback to cached if updateContentText returns empty
  }, [contentText, updateContentText]);

  // Get selected text from the document (if user has highlighted/selected text)
  const getSelection = useCallback(async (): Promise<string> => {
    if (!context) {
      return "";
    }
    
    try {
      const textSelected = await context.getSelection();
      return textSelected || "";
    } catch (error) {
      logger.warn("[useOfficeContent] Error getting selection:", error);
      return ""; // Return empty string on error
    }
  }, [context]);

  // Update content when context, mode, or item changes
  useEffect(() => {
    if (isReady) {
      // For Outlook, we still check item for backward compatibility
      // For other apps (Word/Excel/PowerPoint), context is sufficient
      if (context || item) {
        logger.debug("[useOfficeContent] Triggering content update:", {
          hasContext: !!context,
          hasItem: !!item,
          mode,
          host: context?.host
        });
        updateContentText();
      }
    }
  }, [isReady, context, item, mode, updateContentText]);

  return { 
    contentText, // Renamed from emailBodyText for clarity
    emailBodyText: contentText, // Keep for backward compatibility
    hasOriginalEmail, 
    updateContentText,
    updateEmailBodyText: updateContentText, // Keep for backward compatibility
    getCurrentContentText,
    getCurrentEmailBodyText: getCurrentContentText, // Keep for backward compatibility
    getSelection // Get selected/highlighted text from document
  };
}